'use client';

import {
  forwardRef,
  useEffect,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  CustomImage,
  getSpeedsizeImageUrl,
} from 'components/shared/custom-image/CustomImage';
import type { MeasureFeature, VideoFit } from './measureMedia';
import styles from './MeasureFeatureMedia.module.css';

const FADE_MS = 350;
const DECODE_TIMEOUT_MS = 1200;
const STAGE = { w: 640, h: 620 } as const;
/** Phone corner radius as a share of the phone's width. */
const PHONE_CORNER = 0.165;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, ms));

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const saveData = () =>
  (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
    ?.saveData === true;

async function decoded(img: HTMLImageElement | null) {
  if (!img) return false;
  try {
    await Promise.race([
      img.decode(),
      sleep(DECODE_TIMEOUT_MS).then(() => Promise.reject(new Error('slow'))),
    ]);
    return img.naturalWidth > 0;
  } catch {
    return false;
  }
}

const pct = (n: number) => `${+n.toFixed(3)}%`;

function fitStyles({ frame, video, corners }: VideoFit) {
  const [fx, fy, fw, fh] = frame;
  const [vx, vy, vw, vh] = video;
  const rx = PHONE_CORNER * 100;
  const ry = ((PHONE_CORNER * fw * STAGE.w) / (fh * STAGE.h)) * 100;
  const radius =
    corners === 'all'
      ? `${pct(rx)} / ${pct(ry)}`
      : `${pct(rx)} ${pct(rx)} 0 0 / ${pct(ry)} ${pct(ry)} 0 0`;
  return {
    frame: {
      left: pct(fx),
      top: pct(fy),
      width: pct(fw),
      height: pct(fh),
      borderRadius: radius,
    } satisfies CSSProperties,
    video: {
      left: pct(((vx - fx) / fw) * 100),
      top: pct(((vy - fy) / fh) * 100),
      width: pct((vw / fw) * 100),
      height: pct((vh / fh) * 100),
    } satisfies CSSProperties,
  };
}

/** Mounted only while its card is the settled one, so it always plays from 0 and only once. */
function MeasureFeatureVideo({ src, fit }: { src: string; fit: VideoFit }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const geometry = fitStyles(fit);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || prefersReducedMotion() || saveData()) return undefined;
    el.muted = true;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !el.ended) {
          void el.play().catch(() => undefined);
        } else {
          el.pause();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      el.pause();
    };
  }, []);

  return (
    <div className={styles.videoFrame} style={geometry.frame} aria-hidden>
      <video
        ref={videoRef}
        className={`${styles.video} ${playing ? styles.videoOn : ''}`}
        style={geometry.video}
        src={getSpeedsizeImageUrl(src)}
        preload="none"
        muted
        playsInline
        onPlaying={() => setPlaying(true)}
      />
    </div>
  );
}

export const MeasureFeatureMedia = forwardRef<
  HTMLImageElement,
  {
    feature: MeasureFeature;
    imageAlt: string;
    sizes: string;
    /** CORS-readable, so the wipe can upload it as a texture. */
    stage?: boolean;
    eager?: boolean;
    /** The card on screen and at rest; a video plays only then. */
    active?: boolean;
  }
>(function MeasureFeatureMedia(
  { feature, imageAlt, sizes, stage, eager, active = false },
  ref,
) {
  const media = feature.media;
  if (!media) return null;

  return (
    <>
      <CustomImage
        ref={ref}
        src={media.kind === 'video' ? media.poster : media.src}
        alt={`${imageAlt}: ${feature.title}`}
        fill
        className={styles.asset}
        sizes={sizes}
        crossOrigin={stage ? 'anonymous' : undefined}
        loading={eager ? 'eager' : undefined}
      />
      {active && media.kind === 'video' ? (
        <MeasureFeatureVideo src={media.src} fit={media.fit} />
      ) : null}
    </>
  );
});

type Slots = readonly [MeasureFeature | null, MeasureFeature | null];

type Stage = {
  held: Slots;
  front: 0 | 1;
  /** Transition in progress; its target sits in the back slot. */
  flight: MeasureFeature | null;
  /** Picks waiting their turn, oldest first, each feature at most once. */
  queue: MeasureFeature[];
};

type StageAction = { type: 'pick'; feature: MeasureFeature } | { type: 'land' };

const sameMedia = (a: MeasureFeature, b: MeasureFeature) =>
  a.id === b.id && a.media?.src === b.media?.src;

/** Starts the next distinct pick, skipping queued heads that are already on screen. */
function advance(stage: Stage): Stage {
  const shown = stage.held[stage.front];
  const queue = [...stage.queue];
  while (queue.length && shown && sameMedia(queue[0], shown)) queue.shift();
  return { ...stage, flight: queue.shift() ?? null, queue };
}

function stageReducer(stage: Stage, action: StageAction): Stage {
  if (action.type === 'land') {
    if (!stage.flight) return stage;
    const held: Slots =
      stage.front === 0
        ? [stage.held[0], stage.flight]
        : [stage.flight, stage.held[1]];
    return advance({ ...stage, held, front: stage.front === 0 ? 1 : 0 });
  }

  const tail = stage.queue.at(-1) ?? stage.flight ?? stage.held[stage.front];
  if (tail && sameMedia(tail, action.feature)) return stage;
  // A repeat moves to the end, so the last entry is always the latest pick.
  const next = {
    ...stage,
    queue: [
      ...stage.queue.filter((queued) => !sameMedia(queued, action.feature)),
      action.feature,
    ],
  };
  return stage.flight ? next : advance(next);
}

export function MeasureFeatureMediaStage({
  feature,
  hint,
  imageAlt,
  sizes,
  imageLeft,
}: {
  feature: MeasureFeature;
  /** Feature the visitor is about to pick; loads into the idle slot. */
  hint?: MeasureFeature;
  imageAlt: string;
  sizes: string;
  /** Copy sits on the right, so the wipe travels right to left. */
  imageLeft: boolean;
}) {
  const [stage, dispatch] = useReducer(
    stageReducer,
    feature,
    (first): Stage => ({
      held: [first, null],
      front: 0,
      flight: null,
      queue: [],
    }),
  );
  const [fading, setFading] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const imgs = useRef<(HTMLImageElement | null)[]>([null, null]);
  const waiting = useRef(0);

  const { held, front, flight, queue } = stage;
  const back = front === 0 ? 1 : 0;
  const shown = held[front] ?? feature;
  const idleHint = !flight && hint && !sameMedia(hint, shown) ? hint : null;
  const backFeature = flight ?? idleHint ?? held[back];

  useEffect(() => {
    waiting.current = queue.length;
  });

  useEffect(() => {
    dispatch({ type: 'pick', feature });
  }, [feature]);

  useEffect(() => {
    if (!flight) return undefined;
    const ctl = new AbortController();

    const run = async () => {
      const ready = await decoded(imgs.current[back]);
      if (ctl.signal.aborted) return;
      if (!ready || prefersReducedMotion()) {
        dispatch({ type: 'land' });
        return;
      }

      try {
        const host = boxRef.current;
        const from = imgs.current[front];
        const to = imgs.current[back];
        if (host && from && to) {
          const wipe = await import('./wipeTransition');
          if (ctl.signal.aborted) return;
          if (wipe.wipeSupported()) {
            const result = await wipe.playWipe({
              host,
              from,
              to,
              dir: imageLeft ? -1 : 1,
              className: styles.canvas,
              signal: ctl.signal,
              ms: waiting.current ? wipe.WIPE.ms / 2 : wipe.WIPE.ms,
            });
            if (ctl.signal.aborted) return;
            if (result === 'done') {
              dispatch({ type: 'land' });
              wipe.releaseWipe(styles.canvasOut);
              return;
            }
          }
        }
      } catch {
        // chunk failed to load: fall back to the fade
      }

      if (ctl.signal.aborted) return;
      setFading(true);
      await sleep(FADE_MS);
      if (ctl.signal.aborted) return;
      setFading(false);
      dispatch({ type: 'land' });
    };
    void run();

    return () => ctl.abort();
  }, [flight, front, back, imageLeft]);

  const hintKey = hint?.id;
  useEffect(() => {
    if (!hintKey || prefersReducedMotion()) return;
    void import('./wipeTransition').then((wipe) => wipe.warmWipe());
  }, [hintKey]);

  return (
    <div
      ref={boxRef}
      className={styles.stage}
      style={{ '--stage-fade-ms': `${FADE_MS}ms` } as CSSProperties}
    >
      {([0, 1] as const).map((slot) => {
        const isFront = slot === front;
        const content = isFront ? shown : backFeature;
        if (!content) return null;
        const role = isFront
          ? styles.front
          : fading
            ? styles.fadeIn
            : styles.back;
        return (
          <div
            key={slot}
            className={`${styles.slot} ${role}`}
            aria-hidden={isFront ? undefined : true}
          >
            <MeasureFeatureMedia
              ref={(el) => {
                imgs.current[slot] = el;
              }}
              feature={content}
              imageAlt={imageAlt}
              sizes={sizes}
              stage
              eager={!isFront}
              active={isFront && !flight}
            />
          </div>
        );
      })}
    </div>
  );
}
